import { gatedFetch } from '../utils/requestGate';
import { IS_FACTURACION } from '../config';

/** Producción / APK Capacitor: nunca dejar base vacía (salta a https://localhost y falla el ping). */
const PROD_API = 'https://api.ohbellezaoficial.com';
const RAW_API = String(process.env.REACT_APP_API_URL || '').trim().replace(/\/$/, '');
const DEFAULT_TIMEOUT_MS = 25000;
const TOKEN_KEY = 'oh_bodega_token';

function isNativeShell() {
  try {
    const h = String(window?.location?.hostname || '');
    const p = String(window?.location?.protocol || '');
    return p === 'capacitor:' || p === 'ionic:' || h === 'localhost' || h === '127.0.0.1';
  } catch {
    return false;
  }
}

/** En APK siempre HTTPS absoluto aunque el build se haya hecho sin .env. */
export function resolveApiBase() {
  if (RAW_API) {
    // Tablets nuevas bloquean cleartext: forzar https si alguien dejó http://
    if (/^http:\/\//i.test(RAW_API) && /ohbellezaoficial\.com/i.test(RAW_API)) {
      return RAW_API.replace(/^http:\/\//i, 'https://');
    }
    return RAW_API;
  }
  if (process.env.NODE_ENV === 'production' || isNativeShell()) return PROD_API;
  return '';
}

function imageBase() {
  return resolveApiBase() || (process.env.NODE_ENV !== 'production' ? 'http://localhost:8081' : PROD_API);
}

let _token = null;

export function setAuthToken(token) {
  _token = token ? String(token) : null;
  try {
    if (_token) sessionStorage.setItem(TOKEN_KEY, _token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function getAuthToken() {
  if (_token) return _token;
  try {
    _token = sessionStorage.getItem(TOKEN_KEY) || null;
  } catch {
    _token = null;
  }
  return _token;
}

export function clearAuthToken() {
  _token = null;
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

function authHeaders() {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(path, options = {}) {
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null;

  let res;
  const base = resolveApiBase();
  try {
    res = await gatedFetch(`${base}/api${path}`, {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...authHeaders(),
        ...(options.headers || {}),
      },
      body: options.body,
      signal: controller?.signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') {
      throw new Error('La solicitud tardó demasiado. Revisa la conexión e intenta de nuevo.');
    }
    const sslHint = isNativeShell()
      ? ' En esta tablet: Fecha y hora automática ON; en el Wi‑Fi pon DNS 8.8.8.8 y 8.8.4.4 (no hace falta DNS privado). Prueba con el hotspot del celular.'
      : '';
    throw new Error(
      `No se pudo conectar con el servidor (${base || 'sin URL'}). Confirma Wi‑Fi e internet.${sslHint}`
    );
  } finally {
    if (timer) clearTimeout(timer);
  }

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const fallback =
      res.status >= 500
        ? `Error del servidor (${res.status}). Intenta de nuevo.`
        : `Error en la solicitud (${res.status})`;
    const err = new Error(json.error || fallback);
    err.status = res.status;
    err.code = json.code || undefined;
    err.data = json;
    throw err;
  }
  return json;
}

export function formatCOP(value) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);
}

export function imageUrl(imagen) {
  if (!imagen) return null;
  if (imagen.startsWith('http') || imagen.startsWith('data:')) return imagen;
  const path = String(imagen).replace(/^\//, '');
  const base = imageBase() || '';
  if (!base) return `/${path}`;
  return `${base.replace(/\/$/, '')}/${path}`;
}

export const api = {
  ping: () => request('/ping'),
  loginPin: async (pin) => {
    const res = await request(IS_FACTURACION ? '/facturacion/pin-login' : '/bodega/pin-login', {
      method: 'POST',
      body: JSON.stringify({ pin: String(pin || '').trim() }),
    });
    if (res.data?.token) setAuthToken(res.data.token);
    return res;
  },
  login: async (username, password) => {
    const res = await request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        username: String(username || '').trim(),
        password: String(password || ''),
        app: IS_FACTURACION ? 'facturacion' : 'bodega',
      }),
    });
    if (res.data?.token) setAuthToken(res.data.token);
    return res;
  },
  me: () => request('/auth/me'),
  logout: () => request('/auth/logout', { method: 'POST' }).finally(() => clearAuthToken()),
  getSeparadores: () =>
    request(IS_FACTURACION ? '/facturacion/equipo' : '/bodega/separadores'),
  getPedidos: ({ estado, q, limit = 100 } = {}) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (IS_FACTURACION) {
      if (estado) params.set('facturacion_estado', estado);
    } else if (estado) {
      params.set('estado', estado);
    }
    if (q) params.set('q', q);
    return request(`${IS_FACTURACION ? '/facturacion/pedidos' : '/bodega/pedidos'}?${params}`);
  },
  getPedido: (id) =>
    request(IS_FACTURACION ? `/facturacion/pedidos/${id}` : `/bodega/pedidos/${id}`),
  getHistorial: ({ fecha } = {}) => {
    const params = new URLSearchParams({
      ...(IS_FACTURACION ? { facturacion_estado: 'facturado' } : { estado: 'entregado' }),
      limit: '50',
    });
    if (fecha) {
      params.set('desde', fecha);
      params.set('hasta', fecha);
    }
    return request(`${IS_FACTURACION ? '/facturacion/pedidos' : '/bodega/pedidos'}?${params}`);
  },
  saveCanasta: (id, keys, separadorNombre, revision, missingKeys = []) =>
    request(IS_FACTURACION ? `/facturacion/pedidos/${id}/canasta` : `/bodega/pedidos/${id}/canasta`, {
      method: 'PATCH',
      body: JSON.stringify({
        keys,
        missing_keys: Array.isArray(missingKeys) ? missingKeys : [],
        separador_nombre: IS_FACTURACION ? undefined : separadorNombre || undefined,
        revision: revision != null ? revision : undefined,
      }),
    }),
  iniciarFacturacion: (id, { facturadorId, facturadorNombre } = {}) =>
    request(`/facturacion/pedidos/${id}/iniciar`, {
      method: 'PATCH',
      body: JSON.stringify({
        facturador_id: facturadorId || undefined,
        facturador_nombre: facturadorNombre || undefined,
      }),
    }),
  completarFacturacion: (id, { facturadorId, facturadorNombre } = {}) =>
    request(`/facturacion/pedidos/${id}/completar`, {
      method: 'PATCH',
      body: JSON.stringify({
        facturador_id: facturadorId || undefined,
        facturador_nombre: facturadorNombre || undefined,
      }),
    }),
  saveNotasBodega: (id, notasBodega, separadorNombre) =>
    request(`/bodega/pedidos/${id}/notas`, {
      method: 'PATCH',
      body: JSON.stringify({
        notas_bodega: notasBodega,
        separador_nombre: separadorNombre || undefined,
      }),
    }),
  actualizarEstado: (
    id,
    estado,
    { separadorId, separadorNombre, revisadorId, revisadorNombre, firmaRevision } = {}
  ) =>
    request(`/bodega/pedidos/${id}/estado`, {
      method: 'PATCH',
      body: JSON.stringify({
        estado: String(estado),
        separador_id: separadorId || undefined,
        separador_nombre: separadorNombre || undefined,
        revisador_id: revisadorId || undefined,
        revisador_nombre: revisadorNombre || undefined,
        firma_revision: firmaRevision === true ? true : undefined,
      }),
    }),
};
